import React from 'react';
import { createLogger } from './utils/logger';

const logger = createLogger('ToastProvider');

const ToastContext = React.createContext(null);

let toastIdCounter = 0;

export function ToastProvider({ children }) {
	const [toasts, setToasts] = React.useState([]);

	const removeToast = React.useCallback((id) => {
		setToasts((current) => current.filter((t) => t.id !== id));
	}, []);

	const showToast = React.useCallback((toast) => {
		const id = ++toastIdCounter;
		const next = {
			id,
			type: toast.type || 'error',
			title: toast.title || 'Error',
			message: toast.message || '',
			details: toast.details || '',
			timeoutMs: toast.timeoutMs ?? 8000
		};
		logger.debug('showToast called', next);
		setToasts((current) => [...current, next]);

		if (next.timeoutMs > 0) {
			setTimeout(() => removeToast(id), next.timeoutMs);
		}

		return id;
	}, [removeToast]);

	const contextValue = React.useMemo(
		() => ({
			showToast,
			removeToast
		}),
		[showToast, removeToast]
	);

	async function handleToastClick(e, toast) {
		e.stopPropagation();
		const text =
			toast.details ||
			toast.message ||
			toast.title ||
			'No additional error details available.';

		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(text);
			}
		} catch {
			// Best-effort copy; ignore clipboard errors
		}
	}

	return (
		<ToastContext.Provider value={contextValue}>
			{children}
			<div className="toast-container" aria-live="polite" aria-atomic="true">
				{toasts.map((toast) => (
					<button
						key={toast.id}
						type="button"
						className={`toast ${toast.type === 'error' ? 'toast-error' : 'toast-info'}`}
						onClick={(e) => handleToastClick(e, toast)}
					>
						<div className="toast-title">{toast.title}</div>
						{toast.message ? <div className="toast-message">{toast.message}</div> : null}
						<div className="toast-hint">Click to copy error details</div>
					</button>
				))}
			</div>
		</ToastContext.Provider>
	);
}

export function useToast() {
	const ctx = React.useContext(ToastContext);
	if (!ctx) {
		throw new Error('useToast must be used within a ToastProvider');
	}
	return ctx;
}


